import axios from 'axios';
import cn from 'classnames';
import { PostMediaResult, User } from 'common/interfaces/api';
import FileSelect from 'components/common/FileSelect';
import { PenIcon } from 'components/v2/atoms/icon';
import ImageEditor from 'components/v2/molecules/image-editor';
import Image from 'next/legacy/image';
import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

interface ProfileCoverImagePropsType {
  user: User;
  isCurrentUser: boolean;
  isSettingsPage: boolean;
  onImagePreview: (imageUrl: string) => void;
}

const ProfileCoverImage = ({
  user,
  isCurrentUser,
  isSettingsPage,
  onImagePreview,
}: ProfileCoverImagePropsType) => {
  const refetchProfile = useStore((state) => state.refetchProfile);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );

  // Cover image editor state
  const [openCoverImageEditor, setOpenCoverImageEditor] = useState(false);
  const [coverImageMediaUrl, setCoverImageMediaUrl] = useState<string>();

  const { mutate: updateProfileBackgroundImage } = useMutation(
    (url: string) => {
      return axios.post(`/profiles/background_images`, {
        background_image: url,
      });
    },
    {
      onSuccess: () => {
        void refetchProfile();
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: '背景画像の更新が失敗されました',
        });
      },
    },
  );

  const { mutate: uploadCoverImage, isLoading: isUploadingCoverImage } =
    useMutation(
      async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return (
          await axios.post(`/posts/media`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        ).data as PostMediaResult;
      },
      {
        onSuccess: (data) => {
          updateProfileBackgroundImage(data?.data?.data.url);
        },
        onError: () => {
          setSnackbarMessage({
            type: 'error',
            text: '背景画像のアップロードが失敗されました',
          });
        },
      },
    );

  const handleCoverImageSelect = useCallback((file: File) => {
    setCoverImageMediaUrl(URL.createObjectURL(file));
    setOpenCoverImageEditor(true);
  }, []);

  const isBackgroundImage =
    user.background_image && user.background_image?.startsWith('http');

  return (
    <div className="relative h-[200px] w-full border-b border-pp-border-primary bg-pp-bg-tertiary">
      {isBackgroundImage ? (
        <div
          className="relative size-full cursor-pointer"
          onClick={() => onImagePreview(user.background_image)}
        >
          <Image
            src={user?.background_image}
            layout="fill"
            objectFit="cover"
            alt={user?.name || 'Cover image'}
            priority
          />
        </div>
      ) : (
        <div className="size-full bg-gradient-to-r from-pp-bg-tertiary to-gray-quaternary" />
      )}

      {/* Edit Cover Image Button - only show for current user */}
      {isCurrentUser && isSettingsPage && (
        <FileSelect
          id="cover-image-upload"
          onFileSelect={handleCoverImageSelect}
          accept=".jpeg,.jpg,.png"
          className="absolute right-3 top-3 z-10 cursor-pointer"
        >
          <div
            className={cn(
              'flex size-9 items-center justify-center rounded-full border border-white bg-pp-overlay-dark text-white shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:bg-pp-overlay-dark-hover',
              { 'pointer-events-none opacity-50': isUploadingCoverImage },
            )}
          >
            <PenIcon className="size-4" />
          </div>
        </FileSelect>
      )}

      {/* Cover Image Editor */}
      {openCoverImageEditor && coverImageMediaUrl && isSettingsPage && (
        <ImageEditor
          open={openCoverImageEditor}
          setOpen={setOpenCoverImageEditor}
          mediaUrl={coverImageMediaUrl}
          fixedRatio={16 / 5}
          freeCrop={false}
          onEditComplete={(file) => {
            uploadCoverImage(file);
            setOpenCoverImageEditor(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileCoverImage;
